import { useMemo, useState } from "react";
import { localizePeriodLabel } from "../../i18n/period-labels";
import { withPercent } from "../../i18n/number-format";

const COPY = {
  en: {
    title: "Period Management",
    subtitle: "Track whether the period is on schedule, what remains, and when it is ready to close.",
    selectedCycle: "Period",
    newCycle: "New period",
    cycleName: "Period name",
    start: "Start date",
    end: "End date",
    create: "Create period",
    cancel: "Cancel",
    active: "Active",
    draft: "Draft",
    locked: "Closed · Locked",
    finalizing: "Finalizing",
    calendarProgress: "Calendar progress",
    evaluationProgress: "Evaluation completion",
    activities: "Evaluation activities",
    needsAttention: "Needs attention",
    noAttention: "Everything is progressing without outstanding items.",
    closing: "Ready to close",
    readyToClose: "Ready to finalize",
    notReady: "Outstanding items must be completed before finalizing.",
    activate: "Activate period",
    finalize: "Finalize and lock period",
    reopen: "Reopen period",
    completed: "completed",
    pending: "remaining",
    viewDetails: "View details",
    hideDetails: "Hide details",
    noRemaining: "No remaining items.",
    setup: "Period setup",
    setupHint: "This draft period is being prepared. Organization templates and CVI model settings are managed outside Period Management.",
    datesReady: "Period dates defined",
    routineReady: "Core responsibility setup available",
    indicatorsReady: "Collective indicators configured",
    defaultsReady: "Organization defaults inherited",
    goals: "Individual Goals",
    routine: "Core Responsibilities",
    activitiesComp: "Additional Contributions",
    peer: "Peer Feedback",
    indicators: "Collective Indicators",
    managerReview: "Manager Review",
    goalAwaitingConfirmation: "Awaiting confirmation",
    goalIncomplete: "Not completed",
    activityAwaitingReview: "Awaiting review",
    peerReviewPendingFor: "Feedback pending for",
    indicatorAwaitingApproval: "Awaiting approval",
    indicatorDataMissing: "Data not submitted",
    routineIncomplete: "Not completed",
    managerReviewPending: "Manager review pending",
    finalizedOn: "Finalized",
    historyNote: "This period is locked and read-only.",
    eyebrow: "Period & Operations",
    noCycle: "No period has been created yet. Create the first period to start planning.",
    overall: "Overall",
    invalidRange: "The end date must be after the start date.",
    working: "Working…",
  },
  fr: {
    title: "Gestion des périodes",
    subtitle: "Vérifiez l’avancement de la période, les éléments restants et son état de clôture.",
    selectedCycle: "Période",
    newCycle: "Nouvelle période",
    cycleName: "Nom de la période",
    start: "Date de début",
    end: "Date de fin",
    create: "Créer la période",
    cancel: "Annuler",
    active: "Active",
    draft: "Brouillon",
    locked: "Clôturée · Verrouillée",
    finalizing: "Finalisation",
    calendarProgress: "Progression du calendrier",
    evaluationProgress: "Avancement de l’évaluation",
    activities: "Activités d’évaluation",
    needsAttention: "À surveiller",
    noAttention: "Tout progresse sans élément en attente.",
    closing: "Prête pour la clôture",
    readyToClose: "Prête à finaliser",
    notReady: "Les éléments en attente doivent être complétés avant la finalisation.",
    activate: "Activer la période",
    finalize: "Finaliser et verrouiller la période",
    reopen: "Rouvrir la période",
    completed: "terminés",
    pending: "restants",
    viewDetails: "Voir les détails",
    hideDetails: "Masquer les détails",
    noRemaining: "Aucun élément restant.",
    setup: "Préparation de la période",
    setupHint: "Cette période est en préparation. Les modèles de l’organisation et les paramètres du modèle CVI sont gérés en dehors de la Gestion des périodes.",
    datesReady: "Dates de la période définies",
    routineReady: "Configuration des responsabilités disponible",
    indicatorsReady: "Indicateurs collectifs configurés",
    defaultsReady: "Paramètres par défaut hérités",
    goals: "Objectifs individuels",
    routine: "Responsabilités principales",
    activitiesComp: "Contributions additionnelles",
    peer: "Rétroaction des collègues",
    indicators: "Indicateurs collectifs",
    managerReview: "Évaluation du gestionnaire",
    goalAwaitingConfirmation: "En attente de confirmation",
    goalIncomplete: "Non terminé",
    activityAwaitingReview: "En attente de révision",
    peerReviewPendingFor: "Rétroaction en attente pour",
    indicatorAwaitingApproval: "En attente d’approbation",
    indicatorDataMissing: "Données non soumises",
    routineIncomplete: "Non terminé",
    managerReviewPending: "Évaluation du gestionnaire en attente",
    finalizedOn: "Finalisée",
    historyNote: "Cette période est verrouillée et en lecture seule.",
    eyebrow: "Période et opérations",
    noCycle: "Aucune période n’a encore été créée. Créez la première période pour commencer la planification.",
    overall: "Global",
    invalidRange: "La date de fin doit être postérieure à la date de début.",
    working: "Traitement…",
  },
};

const componentLabel = (copy, id) => id === "activities" ? copy.activitiesComp : (copy[id] || id);

function normalizeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
function progressFor(period) {
  const start = normalizeDate(period?.startDate || period?.start);
  const end = normalizeDate(period?.endDate || period?.end);
  if (!start || !end || end <= start) return null;
  return Math.max(0, Math.min(100, Math.round(((new Date() - start) / (end - start)) * 100)));
}
function dateText(value, locale) {
  const date = normalizeDate(value);
  if (!date) return "—";
  try { return new Intl.DateTimeFormat(locale === "fr" ? "fr-CA" : "en-CA", { year:"numeric", month:"short", day:"numeric" }).format(date); }
  catch { return String(value); }
}
function ProgressBar({ value, label }) {
  const safe = Math.max(0, Math.min(100, Number(value) || 0));
  return <div className="adm-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(safe)} aria-label={label}>
    <span style={{ width: `${safe}%` }} />
  </div>;
}
function Check({ ok }) {
  return <span className={`adm-check-mark${ok ? " is-done" : ""}`} aria-hidden="true">{ok ? "✓" : ""}</span>;
}

export default function CycleManagement({
  locale = "en",
  periods = [],
  selectedPeriodId = "",
  onPeriodChange,
  cycleStatus = null,
  loading = false,
  onCreateCycle,
  onActivate,
  onFinalize,
  onReopen,
}) {
  const copy = COPY[locale] || COPY.en;
  const direction = "ltr";
  const [expanded, setExpanded] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCycle, setNewCycle] = useState({ label:"", start:"", end:"" });
  const period = cycleStatus?.period || periods.find(p => p.id === selectedPeriodId) || null;
  const components = cycleStatus?.components || [];
  const closing = cycleStatus?.closing;
  const [busy, setBusy] = useState("");
  const periodStart = period?.startDate || period?.start, periodEnd = period?.endDate || period?.end;
  const calendar = useMemo(() => progressFor({ startDate: periodStart, endDate: periodEnd }), [periodStart, periodEnd]);
  const invalidRange = Boolean(newCycle.start && newCycle.end && newCycle.end <= newCycle.start);
  // Every period action is asynchronous; one at a time, and the button says it is working.
  const run = async (key, action) => {
    if (busy) return;
    setBusy(key);
    try { await action(); } finally { setBusy(""); }
  };
  const status = String(period?.status || "").toLowerCase();
  const statusText = status === "active" ? copy.active : status === "locked" ? copy.locked : status === "finalizing" ? copy.finalizing : copy.draft;
  const overall = cycleStatus?.overallProgress ?? 0;
  const config = cycleStatus?.configuration || {};
  const attention = components.filter(item => item.pending > 0);

  const createCycle = () => run("create", async () => {
    if (!newCycle.label.trim() || !newCycle.start || !newCycle.end || invalidRange || !onCreateCycle) return;
    const created = await onCreateCycle({ ...newCycle, label: newCycle.label.trim() });
    if (created) {
      setNewCycle({ label:"", start:"", end:"" });
      setCreating(false);
    }
  });

  const statusTone = status === "active" ? "success" : status === "locked" ? "navy" : status === "finalizing" ? "warning" : "neutral";

  return <section dir={direction} className="adm-page adm-cycle" data-cvi-i18n="managed">
    <header className="adm-page-header">
      <div className="adm-page-header-copy">
        <div className="adm-eyebrow">{copy.eyebrow}</div>
        <h1 className="adm-title">{copy.title}</h1>
        <p className="adm-desc">{copy.subtitle}</p>
      </div>
      <div className="adm-header-actions">
        <label className="adm-field" style={{ minWidth: 240 }}>
          <span className="adm-sr">{copy.selectedCycle}</span>
          <select aria-label={copy.selectedCycle} value={selectedPeriodId || ""} onChange={e => onPeriodChange?.(e.target.value)}>
            {periods.map(p => <option key={p.id} value={p.id}>{localizePeriodLabel(p.label || p.name, locale)}{p.active ? (locale==="fr"?" — actuel":" — current") : ""}</option>)}
          </select>
        </label>
        <button type="button" className="adm-btn adm-btn--primary" aria-expanded={creating} onClick={() => setCreating(v => !v)}>+ {copy.newCycle}</button>
      </div>
    </header>

    {creating && <section className="adm-panel">
      <div className="adm-panel-head"><div><h2 className="adm-panel-title">{copy.newCycle}</h2></div></div>
      <div className="adm-panel-body">
        <div className="adm-form-grid is-3">
          <label className="adm-field"><span>{copy.cycleName}<span className="adm-required" aria-hidden="true">*</span></span><input value={newCycle.label} onChange={e=>setNewCycle(x=>({...x,label:e.target.value}))} /></label>
          <label className="adm-field"><span>{copy.start}<span className="adm-required" aria-hidden="true">*</span></span><input type="date" value={newCycle.start} onChange={e=>setNewCycle(x=>({...x,start:e.target.value}))} /></label>
          <label className="adm-field"><span>{copy.end}<span className="adm-required" aria-hidden="true">*</span></span><input type="date" value={newCycle.end} min={newCycle.start || undefined} aria-invalid={invalidRange || undefined} onChange={e=>setNewCycle(x=>({...x,end:e.target.value}))} />{invalidRange && <span className="adm-error" role="alert">{copy.invalidRange}</span>}</label>
        </div>
      </div>
      <div className="adm-panel-foot">
        <button type="button" className="adm-btn" onClick={()=>setCreating(false)}>{copy.cancel}</button>
        <button type="button" className="adm-btn adm-btn--primary" disabled={!newCycle.label.trim() || !newCycle.start || !newCycle.end || invalidRange || Boolean(busy)} onClick={createCycle}>{busy === "create" ? copy.working : copy.create}</button>
      </div>
    </section>}

    {!period ? <section className="adm-panel"><div className="adm-empty"><strong>{copy.newCycle}</strong><span>{copy.noCycle}</span></div></section> : <>
      <section className="adm-panel">
        <div className="adm-panel-body adm-cycle-summary">
          <div style={{ minWidth: 0 }}>
            <div className="adm-row">
              <h2 className="adm-panel-title" style={{ fontSize: 20 }}>{localizePeriodLabel(period.name || periods.find(p=>p.id===selectedPeriodId)?.label || "—", locale)}</h2>
              <span className={`adm-badge adm-badge--${statusTone}`}>{statusText}</span>
            </div>
            <p className="adm-panel-desc">{dateText(period.startDate || period.start, locale)} – {dateText(period.endDate || period.end, locale)}</p>
          </div>
          <div className="adm-cycle-metrics">
            <div className="adm-progress-row"><strong>{copy.calendarProgress}</strong><span>{calendar == null ? "—" : withPercent(calendar, locale)}</span><ProgressBar value={calendar ?? 0} label={copy.calendarProgress} /></div>
            <div className="adm-progress-row"><strong>{copy.evaluationProgress}</strong><span>{loading ? "…" : withPercent(overall, locale)}</span><ProgressBar value={overall} label={copy.evaluationProgress} /></div>
          </div>
        </div>
      </section>

      {status !== "locked" && status !== "active" && <section className="adm-panel">
        <div className="adm-panel-head"><div><h2 className="adm-panel-title">{copy.setup}</h2><p className="adm-panel-desc">{copy.setupHint}</p></div></div>
        <div className="adm-panel-body">
          <ul className="adm-checklist">
            {[
              [copy.datesReady, Boolean(period.startDate || period.start) && Boolean(period.endDate || period.end)],
              [copy.routineReady, (config.routineQuestions || 0) > 0],
              [copy.indicatorsReady, (config.indicators || 0) > 0],
              [copy.defaultsReady, true],
            ].map(([text,ok]) => <li key={text}><Check ok={ok} /><span>{text}</span><span /></li>)}
          </ul>
        </div>
        <div className="adm-panel-foot"><button type="button" className="adm-btn adm-btn--primary" disabled={Boolean(busy)} onClick={()=>run("activate", () => onActivate?.(selectedPeriodId))}>{busy === "activate" ? copy.working : copy.activate}</button></div>
      </section>}

      <div className="adm-grid-main-side">
        <section className="adm-panel">
          <div className="adm-panel-head"><div><h2 className="adm-panel-title">{copy.activities}</h2></div></div>
          <div className="adm-panel-body is-flush">
            <ul className="adm-list">
              {components.map(item => {
                const isOpen = expanded === item.id;
                const pct = item.total > 0 ? Math.round((item.completed / item.total) * 100) : 0;
                return <li key={item.id} className="adm-cycle-activity">
                  <div className="adm-list-item" style={{ gridTemplateColumns: "minmax(0,1fr) auto auto" }}>
                    <span>
                      <strong>{componentLabel(copy,item.id)}</strong>
                      <small>{item.total > 0 ? <><b style={{ color: "var(--adm-ink)", fontWeight: 650 }}>{item.completed} / {item.total}</b> {copy.completed}</> : "—"}</small>
                      {item.total > 0 && <span style={{ display: "block", marginTop: 8, maxWidth: 320 }}><ProgressBar value={pct} label={componentLabel(copy,item.id)} /></span>}
                    </span>
                    <span className={`adm-badge ${item.pending > 0 ? "adm-badge--warning" : "adm-badge--success"}`}>{item.pending} {copy.pending}</span>
                    <button type="button" className="adm-btn adm-btn--sm" disabled={!item.pending} aria-expanded={isOpen} onClick={()=>setExpanded(isOpen?"":item.id)}>{isOpen?copy.hideDetails:copy.viewDetails}</button>
                  </div>
                  {isOpen && <div className="adm-cycle-remaining">
                    {!item.remaining?.length ? <div className="adm-hint">{copy.noRemaining}</div> :
                      item.remaining.map(row => <div key={row.id} className="adm-cycle-remaining-row">
                        <div><strong>{row.label}</strong>{row.subject && <small>{row.subject}</small>}</div>
                        <span>{copy[row.detail] || row.detail}{row.detail==="peerReviewPendingFor" && row.subject ? ` · ${row.subject}` : ""}</span>
                      </div>)}
                  </div>}
                </li>;
              })}
            </ul>
          </div>
        </section>

        <div className="adm-stack">
          <section className="adm-panel">
            <div className="adm-panel-head"><div><h2 className="adm-panel-title">{copy.needsAttention}</h2></div>{attention.length > 0 && <span className="adm-badge adm-badge--warning">{attention.length}</span>}</div>
            <div className="adm-panel-body">
              {!attention.length ? <div className="adm-callout is-success"><span aria-hidden="true">✓</span><div>{copy.noAttention}</div></div> :
                <ul className="adm-checklist">{attention.map(item => <li key={item.id}><span className="adm-dot is-warning" aria-hidden="true" style={{ margin: "0 auto" }} /><span style={{ fontWeight: 600 }}>{componentLabel(copy,item.id)}</span><span className="adm-hint">{item.pending} {copy.pending}</span></li>)}</ul>}
            </div>
          </section>

          <section className="adm-panel">
            <div className="adm-panel-head"><div><h2 className="adm-panel-title">{copy.closing}</h2></div></div>
            <div className="adm-panel-body">
              {status === "locked" ? <div className="adm-stack-sm">
                <div className="adm-callout is-neutral"><span aria-hidden="true">🔒</span><div>{copy.historyNote}{period.closedAt && <><br />{copy.finalizedOn}: {dateText(period.closedAt, locale)}</>}</div></div>
              </div> : <>
                <ul className="adm-checklist">
                  {(closing?.checks || []).map(check => <li key={check.id}><Check ok={check.complete} /><span>{componentLabel(copy,check.id)}</span><span /></li>)}
                </ul>
                <div className={`adm-callout ${closing?.ready ? "is-success" : "is-warning"}`} style={{ marginTop: 12 }}><div>{closing?.ready ? copy.readyToClose : copy.notReady}</div></div>
              </>}
            </div>
            {status === "locked" && <div className="adm-panel-foot"><button type="button" className="adm-btn" disabled={Boolean(busy)} onClick={()=>run("reopen", () => onReopen?.(selectedPeriodId))}>{busy === "reopen" ? copy.working : copy.reopen}</button></div>}
            {status === "active" && <div className="adm-panel-foot"><button type="button" className="adm-btn adm-btn--primary" disabled={!closing?.ready || Boolean(busy)} onClick={()=>run("finalize", () => onFinalize?.(selectedPeriodId))}>{busy === "finalize" ? copy.working : copy.finalize}</button></div>}
          </section>
        </div>
      </div>
    </>}
  </section>;
}
